/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: PreviouslyWatched
 * File     : PreviouslyWatched.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the PreviouslyWatched view and coordinating data fetching.
 *   Exports: PreviouslyWatched
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/material
 *   - date-fns
 *   - react
 *
 * Usage:
 *   Use in Router: <Route path="/previouslywatched" element={<PreviouslyWatched />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { getWinnersSummary, buildApiUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Box, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, Chip, Stack } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

const PreviouslyWatched = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user) return;
    getWinnersSummary().then(d => setItems(d.winners || [])).catch(() => {});
  }, [user]);

  if (!user) return <Typography>Login required.</Typography>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Previous Winners</Typography>
      <List>
        {items.filter(w => w && w.pollId && w.movieId).map((w, idx) => (
          <ListItem key={`${w.pollId}-${w.movieId}-${idx}`} divider>
            <ListItemAvatar>
              {(() => {
                const p = w.poster;
                let src = '';
                if (p && p !== 'N/A') {
                  if (p.startsWith('http')) src = p;
                  else src = buildApiUrl(`/api/plex/image?path=${encodeURIComponent(p)}`);
                }
                return <Avatar variant="rounded" src={src} alt={`${w.title} poster`} />;
              })()}
            </ListItemAvatar>
            <ListItemText
              primary={
                w.imdbID ? (
                  <a href={`https://www.imdb.com/title/${w.imdbID}/`} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                    {w.title} ({w.year || ''})
                  </a>
                ) : `${w.title} (${w.year || ''})`
              }
              primaryTypographyProps={{ component: 'div' }}
              secondary={
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`Votes: ${w.votes}`} size="small" />
                  <Chip label={`Won: ${formatDistanceToNow(new Date(w.wonAt))} ago`} size="small" />
                  <Chip label={`Times won: ${w.timesWon}`} size="small" />
                  {typeof w.ratingAvg === 'number' && w.ratingCount > 0 ? (
                    <Chip label={`Avg rating: ${w.ratingAvg} (${w.ratingCount})`} size="small" />
                  ) : null}
                  {w.pollTitle ? <Chip label={`Poll: ${w.pollTitle}`} size="small" /> : null}
                  {w.pollCreatorUsername ? <Chip label={`By: ${w.pollCreatorUsername}`} size="small" /> : null}
                </Stack>
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default PreviouslyWatched;


